import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import {
  apiRequest,
  getApiErrorMessage,
  requestToken,
} from '../lib/api';
import { capabilityValues } from '../types/domain';
import type { Capability, Membership, Profile, Workspace } from '../types/domain';
import { AuthContext } from './auth-context';
import type { AuthState } from './auth-context';

const storageKey = 'brand-field-crew.session';
const workspaceStorageKey = 'brand-field-crew.workspace';
const capabilitySchema = z.enum(capabilityValues);
const tokenPairSchema = z.object({ access: z.string(), refresh: z.string() });
const consentStatusSchema = z.object({
  version: z.string(),
  url: z.string().url(),
  required: z.boolean(),
  accepted: z.boolean(),
  recorded_at: z.string().nullable(),
});

const meSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  email: z.string().email(),
  email_verified: z.boolean(),
  consents: z.object({
    terms: consentStatusSchema,
    privacy: consentStatusSchema,
    marketing: consentStatusSchema,
  }),
  phone: z.string().nullable(),
  avatar: z.string().url().nullable(),
  cnic: z.string().nullable(),
  date_of_birth: z.string().nullable().optional().default(null),
  is_brand_ambassador: z.boolean(),
  is_merchandiser: z.boolean(),
  merchandiser_reference: z.string().nullable(),
  ba_verification_status: z.enum(['draft', 'pending', 'verified', 'rejected', 'suspended']).nullable().optional().default(null),
  is_platform_admin: z.boolean(),
  capabilities: z.array(capabilitySchema),
  memberships: z.array(z.object({
    id: z.string().uuid(),
    organization_id: z.string().uuid(),
    organization_name: z.string(),
    organization_type: z.enum(['agency', 'client', 'platform']),
    organization_logo: z.string().url().nullable().optional().default(null),
    organization_theme_primary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().default('#122F26'),
    organization_theme_accent_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().default('#F0C968'),
    organization_uses_agency_logo: z.boolean().optional().default(false),
    organization_uses_agency_name: z.boolean().optional().default(false),
    role: z.enum(['platform_admin', 'org_admin', 'project_manager', 'viewer', 'supervisor', 'merchandiser']),
    capabilities: z.array(capabilitySchema),
  })),
});

interface StoredSession {
  access: string;
  refresh: string;
}

type CurrentUser = z.infer<typeof meSchema>;

function readSession(): StoredSession | null {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    return parsed.access && parsed.refresh ? { access: parsed.access, refresh: parsed.refresh } : null;
  } catch {
    return null;
  }
}

function mapMembership(row: z.infer<typeof meSchema>['memberships'][number]): Membership {
  return {
    id: row.id,
    organizationId: row.organization_id,
    organizationName: row.organization_name,
    organizationType: row.organization_type,
    organizationLogo: row.organization_logo,
    organizationThemePrimaryColor: row.organization_theme_primary_color,
    organizationThemeAccentColor: row.organization_theme_accent_color,
    organizationUsesAgencyLogo: row.organization_uses_agency_logo,
    organizationUsesAgencyName: row.organization_uses_agency_name,
    role: row.role,
    capabilities: row.capabilities,
  };
}

function buildWorkspaces(
  memberships: Membership[],
  isPlatformAdmin: boolean,
  isBrandAmbassador: boolean,
  accountCapabilities: Capability[],
): Workspace[] {
  const grouped = new Map<string, Workspace>();

  for (const membership of memberships) {
    const existing = grouped.get(membership.organizationId);
    if (existing) {
      if (!existing.roles.includes(membership.role)) existing.roles.push(membership.role);
      existing.capabilities = [...new Set([...existing.capabilities, ...membership.capabilities])];
      continue;
    }
    grouped.set(membership.organizationId, {
      id: membership.organizationId,
      name: membership.organizationName,
      type: membership.organizationType,
      roles: [membership.role],
      capabilities: membership.capabilities,
      organizationId: membership.organizationId,
      logo: membership.organizationLogo,
      themePrimaryColor: membership.organizationThemePrimaryColor ?? '#122F26',
      themeAccentColor: membership.organizationThemeAccentColor ?? '#F0C968',
      usesAgencyLogo: membership.organizationUsesAgencyLogo ?? false,
      usesAgencyName: membership.organizationUsesAgencyName ?? false,
      isGlobal: false,
    });
  }

  const workspaces = [...grouped.values()];
  if (isPlatformAdmin) {
    workspaces.unshift({
      id: 'all-organizations',
      name: 'All organisations',
      type: 'platform',
      roles: ['platform_admin'],
      capabilities: accountCapabilities,
      organizationId: null,
      logo: null,
      isGlobal: true,
    });
  }

  if (isBrandAmbassador) {
    workspaces.push({
      id: 'personal',
      name: 'Personal workspace',
      type: 'personal',
      roles: [],
      capabilities: [
        'overview:view',
        'talent:view',
        'assignments:view',
        'attendance:submit',
        'sales:submit',
        ...accountCapabilities.filter((capability) => capability === 'surveys:submit'),
      ],
      organizationId: null,
      logo: null,
      isGlobal: false,
    });
  } else if (workspaces.length === 0) {
    workspaces.push({
      id: 'personal',
      name: 'Organisation account',
      type: 'personal',
      roles: [],
      capabilities: accountCapabilities,
      organizationId: null,
      logo: null,
      isGlobal: false,
    });
  }

  return workspaces;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => readSession());
  const [profile, setProfile] = useState<Profile | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [accountCapabilities, setAccountCapabilities] = useState<Capability[]>([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    () => localStorage.getItem(workspaceStorageKey),
  );
  const [loading, setLoading] = useState(Boolean(session));
  const applyCurrentUser = useCallback((me: CurrentUser) => {
    setProfile({
      id: me.id,
      fullName: me.full_name,
      email: me.email,
      emailVerified: me.email_verified,
      consents: {
        terms: { ...me.consents.terms, recordedAt: me.consents.terms.recorded_at },
        privacy: { ...me.consents.privacy, recordedAt: me.consents.privacy.recorded_at },
        marketing: { ...me.consents.marketing, recordedAt: me.consents.marketing.recorded_at },
      },
      phone: me.phone,
      avatar: me.avatar,
      cnic: me.cnic,
      dateOfBirth: me.date_of_birth,
      isBrandAmbassador: me.is_brand_ambassador,
      isMerchandiser: me.is_merchandiser,
      merchandiserReference: me.merchandiser_reference,
      baVerificationStatus: me.ba_verification_status,
    });
    setMemberships(me.memberships.map(mapMembership));
    setAccountCapabilities(me.capabilities);
    setIsPlatformAdmin(me.is_platform_admin);
  }, []);

  useEffect(() => {
    if (!session) return;
    let active = true;
    const initialSession: StoredSession = session;

    async function loadCurrentUser() {
      const payload = await apiRequest<unknown>('/auth/me/', { accessToken: initialSession.access });
      return meSchema.parse(payload);
    }

    void loadCurrentUser()
      .then((me) => {
        if (!active) return;
        applyCurrentUser(me);
      })
      .catch(() => {
        localStorage.removeItem(storageKey);
        if (active) setSession(null);
      })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [applyCurrentUser, session]);

  const workspaces = useMemo(
    () => buildWorkspaces(
      memberships,
      isPlatformAdmin,
      profile?.isBrandAmbassador ?? false,
      accountCapabilities,
    ),
    [accountCapabilities, isPlatformAdmin, memberships, profile?.isBrandAmbassador],
  );
  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === selectedWorkspaceId) ?? workspaces[0] ?? null,
    [selectedWorkspaceId, workspaces],
  );

  useEffect(() => {
    if (!selectedWorkspace || selectedWorkspace.id === selectedWorkspaceId) return;
    localStorage.setItem(workspaceStorageKey, selectedWorkspace.id);
  }, [selectedWorkspace, selectedWorkspaceId]);

  const value = useMemo<AuthState>(() => ({
    profile,
    memberships,
    capabilities: selectedWorkspace?.capabilities ?? accountCapabilities,
    isPlatformAdmin,
    workspaces,
    selectedWorkspace,
    accessToken: session?.access ?? null,
    loading,
    selectWorkspace(workspaceId) {
      if (!workspaces.some((workspace) => workspace.id === workspaceId)) return;
      localStorage.setItem(workspaceStorageKey, workspaceId);
      setSelectedWorkspaceId(workspaceId);
    },
    updateWorkspaceAppearance(
      organizationId,
      primaryColor,
      accentColor,
      usesAgencyLogo,
      usesAgencyName,
    ) {
      setMemberships((current) => current.map((membership) => (
        membership.organizationId === organizationId
          ? {
            ...membership,
            organizationThemePrimaryColor: primaryColor,
            organizationThemeAccentColor: accentColor,
            organizationUsesAgencyLogo: usesAgencyLogo,
            organizationUsesAgencyName: usesAgencyName,
            }
          : membership
      )));
    },
    async updateProfile(fields) {
      if (!session) return 'Your session is no longer available.';
      try {
        let body: BodyInit;
        if (fields.avatar) {
          const formData = new FormData();
          formData.set('full_name', fields.fullName);
          formData.set('phone', fields.phone ?? '');
          if (fields.cnic !== undefined) formData.set('cnic', fields.cnic ?? '');
          if (fields.dateOfBirth !== undefined) formData.set('date_of_birth', fields.dateOfBirth ?? '');
          formData.set('avatar', fields.avatar);
          body = formData;
        } else {
          body = JSON.stringify({
            full_name: fields.fullName,
            phone: fields.phone,
            ...(fields.cnic !== undefined ? { cnic: fields.cnic } : {}),
            ...(fields.dateOfBirth !== undefined ? { date_of_birth: fields.dateOfBirth } : {}),
          });
        }
        const payload = await apiRequest<unknown>('/auth/me/', {
          accessToken: session.access,
          method: 'PATCH',
          body,
        });
        applyCurrentUser(meSchema.parse(payload));
        return null;
      } catch (error) {
        return getApiErrorMessage(error);
      }
    },
    async changePassword(fields) {
      if (!session) return 'Your session is no longer available.';
      try {
        const payload = await apiRequest<unknown>('/auth/change-password/', {
          accessToken: session.access,
          method: 'POST',
          body: JSON.stringify({
            current_password: fields.currentPassword,
            new_password: fields.newPassword,
          }),
        });
        const tokens = tokenPairSchema.parse(payload);
        localStorage.setItem(storageKey, JSON.stringify(tokens));
        setSession(tokens);
        return null;
      } catch (error) {
        return getApiErrorMessage(error);
      }
    },
    async resendEmailVerification() {
      if (!session) return 'Your session is no longer available.';
      try {
        await apiRequest('/auth/email-verification/resend/', {
          accessToken: session.access,
          method: 'POST',
        });
        return null;
      } catch (error) {
        return getApiErrorMessage(error);
      }
    },
    async acceptRequiredConsents(fields) {
      if (!session) return 'Your session is no longer available.';
      try {
        const payload = await apiRequest<unknown>('/auth/consents/required/', {
          accessToken: session.access,
          method: 'POST',
          body: JSON.stringify({
            terms_accepted: true,
            privacy_accepted: true,
            terms_version: fields.termsVersion,
            privacy_version: fields.privacyVersion,
          }),
        });
        applyCurrentUser(meSchema.parse(payload));
        return null;
      } catch (error) {
        return getApiErrorMessage(error);
      }
    },
    async updateMarketingConsent(accepted, version) {
      if (!session) return 'Your session is no longer available.';
      try {
        const payload = await apiRequest<unknown>('/auth/consents/marketing/', {
          accessToken: session.access,
          method: 'POST',
          body: JSON.stringify({ accepted, version }),
        });
        applyCurrentUser(meSchema.parse(payload));
        return null;
      } catch (error) {
        return getApiErrorMessage(error);
      }
    },
    async registerBrandAmbassador(fields) {
      try {
        const endpoint = fields.accountType === 'organization' ? '/auth/register/organization/' : '/auth/register/brand-ambassador/';
        const tokens = tokenPairSchema.parse(await apiRequest<unknown>(endpoint, {
          method: 'POST',
          body: JSON.stringify({
            email: fields.email,
            full_name: fields.fullName,
            phone: fields.phone,
            city: fields.city,
            password: fields.password,
            terms_accepted: fields.termsAccepted,
            privacy_accepted: fields.privacyAccepted,
            marketing_consent: fields.marketingConsent,
            terms_version: fields.termsVersion,
            privacy_version: fields.privacyVersion,
            marketing_version: fields.marketingVersion,
          }),
        }));
        localStorage.removeItem(workspaceStorageKey);
        setSelectedWorkspaceId(null);
        localStorage.setItem(storageKey, JSON.stringify(tokens));
        setLoading(true);
        setSession(tokens);
        return null;
      } catch (error) {
        return getApiErrorMessage(error);
      }
    },
    completeGoogleSignIn(payload) {
      const tokens = tokenPairSchema.parse(payload);
      localStorage.setItem(workspaceStorageKey, 'personal');
      setSelectedWorkspaceId('personal');
      localStorage.setItem(storageKey, JSON.stringify(tokens));
      setLoading(true);
      setSession(tokens);
    },
    async signIn(email, password) {
      try {
        const tokens = await requestToken(email, password);
        localStorage.removeItem(workspaceStorageKey);
        setSelectedWorkspaceId(null);
        localStorage.setItem(storageKey, JSON.stringify(tokens));
        setLoading(true);
        setSession(tokens);
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : 'Unable to sign in.';
      }
    },
    signOut() {
      localStorage.removeItem(storageKey);
      setSession(null);
      setProfile(null);
      setMemberships([]);
      setAccountCapabilities([]);
      setIsPlatformAdmin(false);
      setSelectedWorkspaceId(null);
      localStorage.removeItem(workspaceStorageKey);
    },
  }), [
    accountCapabilities,
    applyCurrentUser,
    isPlatformAdmin,
    loading,
    memberships,
    profile,
    selectedWorkspace,
    session,
    workspaces,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
